import { OtpIdentifierType, useRequestMerchantGiftCardOtpMutation } from '@/__generated__/types';
import type { GiftCardContactMethod } from '@/@type/giftCard';
import { type RootState, useDispatch, useSelector } from '@/redux/store';
import { setPreferredContactMethod } from '@/redux/slices/giftCard';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { validateInputType } from '@/utils/regexConfig';
import { matchIsValidTel, type MuiTelInputCountry } from 'mui-tel-input';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveSystemGiftCardPhoneCountry } from '../utils/contactPreferences';

interface UseMerchantGiftCardOtpOptions {
    merchantId?: string;
    requireRedeemCode?: boolean;
}

interface SendOtpOptions {
    redeemCode?: string;
}

interface UseMerchantGiftCardOtpResult {
    contactMethod: GiftCardContactMethod;
    setContactMethod: (value: GiftCardContactMethod) => void;
    email: string;
    setEmail: (value: string) => void;
    phone: string;
    setPhone: (value: string) => void;
    phoneCountry: MuiTelInputCountry;
    setPhoneCountry: (value: MuiTelInputCountry) => void;
    identifier: string;
    identifierLabelKey: 'giftCard.emailAddress' | 'giftCard.phoneNumber';
    verificationCode: string;
    setVerificationCode: (value: string) => void;
    otpId: string;
    isCodeSent: boolean;
    isSendingCode: boolean;
    resendCountdown: number;
    sendOtp: (options?: SendOtpOptions) => Promise<boolean>;
    resetOtpFlow: () => void;
}

const resolveOtpErrorMessage = (error: unknown, t: (key: string) => string): string => {
    if (!error || typeof error !== 'object') {
        return t('giftCard.loadError');
    }

    const apolloError = error as {
        graphQLErrors?: Array<{ message?: string }>;
        message?: string;
    };

    const rawMessage = apolloError.graphQLErrors?.[0]?.message || apolloError.message || '';
    const normalizedMessage = rawMessage.toLowerCase();

    if (normalizedMessage.includes('too many otp requests')) {
        return t('giftCard.tooManyOtpRequests');
    }

    return rawMessage || t('giftCard.loadError');
};

const useMerchantGiftCardOtp = ({ merchantId, requireRedeemCode = false }: UseMerchantGiftCardOtpOptions): UseMerchantGiftCardOtpResult => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbarHelper();
    const dispatch = useDispatch();
    const contactMethodState = useSelector((state: RootState) => state.giftCard.preferredContactMethod);

    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneCountry, setPhoneCountry] = useState<MuiTelInputCountry>(() => resolveSystemGiftCardPhoneCountry());
    const [verificationCode, setVerificationCode] = useState('');
    const [otpId, setOtpId] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);

    const [requestMerchantGiftCardOtpMutation] = useRequestMerchantGiftCardOtpMutation();

    useEffect(() => {
        if (resendCountdown <= 0) {
            return;
        }

        const timerId = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerId);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return (): void => {
            clearInterval(timerId);
        };
    }, [resendCountdown]);

    const setContactMethod = (value: GiftCardContactMethod): void => {
        dispatch(setPreferredContactMethod(value));
    };

    const sendOtp = async (options?: SendOtpOptions): Promise<boolean> => {
        const trimmedIdentifier = contactMethodState === 'email' ? email.trim() : phone.trim();
        const redeemCode = options?.redeemCode?.trim();

        if (!trimmedIdentifier) {
            showSnackbar(t(contactMethodState === 'email' ? 'giftCard.pleaseEnterEmail' : 'giftCard.pleaseEnterPhoneNumber'), 'error');
            return false;
        }

        if (requireRedeemCode && !redeemCode) {
            showSnackbar(t('giftCard.pleaseEnterRedeemCode'), 'error');
            return false;
        }

        if (!merchantId) {
            showSnackbar(t('giftCard.loadError'), 'error');
            return false;
        }

        const isEmail = validateInputType(trimmedIdentifier).isEmail;
        const isPhoneNumber = matchIsValidTel(trimmedIdentifier, { onlyCountries: [phoneCountry] });

        if (contactMethodState === 'email' && !isEmail) {
            showSnackbar(t('giftCard.pleaseEnterValidEmail'), 'error');
            return false;
        }

        if (contactMethodState === 'phone' && !isPhoneNumber) {
            showSnackbar(t('giftCard.pleaseEnterValidPhoneNumber'), 'error');
            return false;
        }

        const otpType = contactMethodState === 'email' ? OtpIdentifierType.Email : OtpIdentifierType.Phone;

        setIsSendingCode(true);
        try {
            const { data: requestOtpData } = await requestMerchantGiftCardOtpMutation({
                variables: {
                    input: {
                        identifier: trimmedIdentifier,
                        merchantId,
                        type: otpType
                    }
                }
            });

            const requestResult = requestOtpData?.requestMerchantGiftCardOtp;
            if (!requestResult?.success) {
                showSnackbar(t('giftCard.loadError'), 'error');
                return false;
            }

            setIsCodeSent(true);
            setOtpId(requestResult.otpId || '');
            setVerificationCode('');
            setResendCountdown(requestResult.expiresIn || 60);
            showSnackbar(t('giftCard.codeSent'), 'success');
            return true;
        } catch (error) {
            showSnackbar(resolveOtpErrorMessage(error, t), 'error');
            return false;
        } finally {
            setIsSendingCode(false);
        }
    };

    const resetOtpFlow = (): void => {
        setIsCodeSent(false);
        setVerificationCode('');
        setOtpId('');
        setResendCountdown(0);
    };

    return {
        contactMethod: contactMethodState,
        setContactMethod,
        email,
        setEmail,
        phone,
        setPhone,
        phoneCountry,
        setPhoneCountry,
        identifier: contactMethodState === 'email' ? email : phone,
        identifierLabelKey: contactMethodState === 'email' ? 'giftCard.emailAddress' : 'giftCard.phoneNumber',
        verificationCode,
        setVerificationCode,
        otpId,
        isCodeSent,
        isSendingCode,
        resendCountdown,
        sendOtp,
        resetOtpFlow
    };
};

export default useMerchantGiftCardOtp;
